/*
 * TEABALLS · Geschenke landing page — reusable gift builder (<tb-gift-builder>, ES module).
 * One component for Maus, LARQ, BRITA filter and Cube (modular-builder brief, 02.11.2026).
 *
 *  - Each instance keeps its own state (hardware option + tea box); changing one module never
 *    changes another. The state survives a visit to a product page (sessionStorage) and is
 *    re-validated against current price/availability; an unavailable choice is never silently
 *    swapped for another product.
 *  - Tea boxes come from the shared library (#tbg-tea-library) in the order configured per module:
 *    three suggestions, "Alle Geschenkboxen ansehen" opens the rest inside the module.
 *  - Prices: current one-time variant prices via TeaballsBFPricing when the campaign is active;
 *    the total is the plain sum of the two components (no extra bundle discount).
 *  - One click adds quantity 1 of each component as one-time purchases through the shared
 *    tb-purchase-core (same drawer/events, shared write lock), both tagged with one
 *    _tb_gift_selection token; the cart is read back and the exact selection confirmed.
 *    Uncertain results are never resent automatically.
 */
import { addPurchase, money } from './tb-purchase-core.js';

const reducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;
const root = () => window.Shopify?.routes?.root || '/';

const readLibrary = () => {
  try {
    return JSON.parse(document.getElementById('tbg-tea-library')?.textContent || '{}');
  } catch (error) {
    return {};
  }
};

const sized = (url, width) => {
  if (!url) return '';
  try {
    const target = new URL(url, window.location.origin);
    target.searchParams.set('width', width);
    return target.href;
  } catch (error) {
    return url;
  }
};

const newToken = () =>
  window.crypto?.randomUUID?.() || `g${Date.now().toString(36)}${Math.random().toString(36).slice(2, 10)}`;

const track = (name, detail = {}) => {
  const payload = { event: name, template: 'collection.gifting', ...detail };
  document.dispatchEvent(new CustomEvent('tbg:event', { detail: payload }));
  if (Array.isArray(window.dataLayer)) window.dataLayer.push(payload);
};

const pricing = () => {
  const bf = window.TeaballsBFPricing;
  const live = Boolean(bf?.active?.());
  return {
    final: (item) => (live ? bf.price(item.id, item.price) : item.price),
    /* same regular (struck-through) price as everywhere in the theme: the comparison reference
       for campaign sets ("gegenüber Einzelkauf"), otherwise the catalogue compare-at price */
    regular: (item) => {
      const compare = Number(item.compare) || 0;
      if (live && bf.regular) return bf.regular(item.id, item.price, compare);
      if (live && bf.reference) return bf.reference(item.id, item.price);
      return Math.max(Number(item.price) || 0, compare);
    },
    paint: (node, base, final, currency) => {
      if (!node) return;
      if (bf?.setAmounts) bf.setAmounts(node, base, final);
      else node.textContent = money(final, currency);
    },
  };
};

class GiftBuilder extends HTMLElement {
  connectedCallback() {
    if (this.ready) return;
    this.ready = true;
    this.$ = (selector) => this.querySelector(selector);
    try {
      this.cfg = JSON.parse(this.$('[data-gift-config]').textContent);
    } catch (error) {
      return;
    }
    this.lib = readLibrary();
    this.currency = this.lib.currency || 'EUR';
    this.words = this.cfg.words || {};
    this.storeKey = `tbg-builder:${location.pathname}:${this.cfg.anchor}`;

    const byKey = new Map((this.lib.teas || []).map((tea) => [tea.key, tea]));
    const wanted = String(this.cfg.teaKeys || '')
      .split(',')
      .map((key) => key.trim().toLowerCase())
      .filter(Boolean);
    this.teas = wanted.length ? wanted.map((key) => byKey.get(key)).filter(Boolean) : [...byKey.values()];
    this.choices = this.cfg.choices || [];
    if (!this.teas.length || !this.choices.length) return;

    const saved = this.readState();
    const firstAvailable = (list) => list.find((item) => item.available && item.id) || list[0];
    this.choiceKey = this.choices.some((c) => c.key === saved.choice) ? saved.choice : firstAvailable(this.choices).key;
    this.teaKey = this.teas.some((t) => t.key === saved.tea) ? saved.tea : firstAvailable(this.teas).key;
    this.expanded = false;
    this.busy = false;
    this.locked = false;

    this.buildTeaList();
    this.$('[data-gift-teas]').hidden = false;
    this.$('[data-gift-summary]').hidden = false;

    this.addEventListener('change', (event) => {
      const input = event.target;
      if (input.matches('[data-gift-choice]')) {
        this.choiceKey = input.value;
        this.afterChange('choice');
      } else if (input.matches('[data-gift-tea]')) {
        this.teaKey = input.value;
        this.afterChange('tea');
        if (this.expanded) {
          this.setExpanded(false);
          this.$('[data-gift-tea-more]')?.focus();
        }
      }
    });
    this.addEventListener('click', (event) => {
      if (event.target.closest('[data-gift-add]')) this.submit();
      if (event.target.closest('[data-gift-tea-more]')) this.setExpanded(!this.expanded);
    });
    document.addEventListener('teaballs:bf-pricing:change', () => this.render());
    this.render();
  }

  readState() {
    try {
      return JSON.parse(sessionStorage.getItem(this.storeKey) || '{}') || {};
    } catch (error) {
      return {};
    }
  }

  writeState() {
    try {
      sessionStorage.setItem(this.storeKey, JSON.stringify({ choice: this.choiceKey, tea: this.teaKey }));
    } catch (error) {
      /* no memory available */
    }
  }

  afterChange(kind) {
    this.locked = false;
    this.say('');
    this.$('[data-gift-again]').hidden = true;
    this.writeState();
    this.render();
    track('gift_builder_change', { module: this.cfg.anchor, field: kind, choice: this.choiceKey, tea: this.teaKey });
  }

  /* ---------- tea chooser ---------- */
  buildTeaList() {
    const list = this.$('[data-gift-tea-list]');
    const name = `tbg-tea-${this.cfg.anchor}`;
    list.id = `tbg-tea-list-${this.cfg.anchor}`;
    list.replaceChildren();
    this.teas.forEach((tea, index) => {
      const label = document.createElement('label');
      label.className = 'tbg-tea';
      label.dataset.rank = String(index);
      if (index < 3) label.dataset.suggest = '';

      const input = document.createElement('input');
      input.type = 'radio';
      input.name = name;
      input.value = tea.key;
      input.dataset.giftTea = '';
      if (!tea.available) input.dataset.unavailable = '';

      const box = document.createElement('span');
      box.className = 'tbg-tea__box';
      if (tea.image) {
        const img = document.createElement('img');
        img.src = sized(tea.image, 160);
        img.alt = '';
        img.width = 56;
        img.height = 56;
        img.loading = 'lazy';
        img.className = 'tbg-tea__img';
        box.append(img);
      }
      const body = document.createElement('span');
      body.className = 'tbg-tea__body';
      const title = document.createElement('span');
      title.className = 'tbg-tea__name';
      title.textContent = tea.name;
      const meta = document.createElement('span');
      meta.className = 'tbg-tea__meta';
      meta.textContent = `${tea.line === 'bio' ? 'BIO · ' : ''}${tea.count} ${this.words.bottles || 'Flaschen'} · ${tea.flavours.join(', ')}`;
      body.append(title, meta);
      const price = document.createElement('span');
      price.className = 'tbg-tea__price';
      price.dataset.teaPrice = tea.key;
      box.append(body, price);
      label.append(input, box);
      list.append(label);
    });
    const more = this.$('[data-gift-tea-more]');
    more.hidden = this.teas.length <= 3;
    more.setAttribute('aria-controls', list.id);
  }

  setExpanded(expanded) {
    this.expanded = expanded;
    const more = this.$('[data-gift-tea-more]');
    more.setAttribute('aria-expanded', String(expanded));
    more.textContent = expanded ? more.dataset.labelClose : more.dataset.labelOpen;
    this.renderTeaVisibility();
  }

  renderTeaVisibility() {
    this.querySelectorAll('.tbg-tea').forEach((label) => {
      const selected = label.querySelector('input').value === this.teaKey;
      label.hidden = !(this.expanded || label.hasAttribute('data-suggest') || selected);
    });
  }

  /* ---------- rendering ---------- */
  get choice() {
    return this.choices.find((c) => c.key === this.choiceKey);
  }

  get tea() {
    return this.teas.find((t) => t.key === this.teaKey);
  }

  isReady() {
    const { choice, tea } = this;
    return Boolean(choice?.id && choice.available && tea?.id && tea.available && String(choice.id) !== String(tea.id));
  }

  render() {
    const { choice, tea } = this;
    if (!choice || !tea) return;
    const price = pricing();

    this.querySelectorAll('[data-gift-choice]').forEach((input) => {
      input.checked = input.value === this.choiceKey;
      input.disabled = this.busy;
    });
    this.querySelectorAll('[data-gift-tea]').forEach((input) => {
      input.checked = input.value === this.teaKey;
      input.disabled = this.busy;
    });
    this.renderTeaVisibility();
    this.$('[data-gift-choice-name]').textContent = ` ${choice.label}`;
    this.$('[data-gift-tea-name]').textContent = ` ${tea.name}`;

    this.teas.forEach((item) => {
      const node = this.querySelector(`[data-tea-price="${CSS.escape(item.key)}"]`);
      if (!node) return;
      if (!item.available) node.textContent = this.words.unavailable;
      else price.paint(node, price.regular(item), price.final(item), this.currency);
    });

    this.renderMedia(choice, tea);

    const hwFinal = price.final(choice);
    const teaFinal = price.final(tea);
    const lineName = tea.line === 'bio' ? 'TEABALLS BIO' : 'TEABALLS';
    const inclusion = tea.inclusion ? ` · ${tea.inclusion.replace(/\.$/, '')}` : '';
    this.item(this.$('[data-gift-line-hw]'), `1 × ${this.cfg.hardwareName}`, choice.label);
    this.item(
      this.$('[data-gift-line-tea]'),
      `1 × ${lineName} ${this.words.teaPrefix} ${tea.name}`,
      `${tea.count} ${this.words.bottles}${inclusion}`
    );
    price.paint(this.$('[data-gift-price-tea]'), price.regular(tea), teaFinal, this.currency);
    if (choice.id) {
      price.paint(this.$('[data-gift-price-hw]'), price.regular(choice), hwFinal, this.currency);
      /* total: regular prices of the selected components added up, against the sum of the final prices */
      price.paint(this.$('[data-gift-total]'), price.regular(choice) + price.regular(tea), hwFinal + teaFinal, this.currency);
    } else {
      /* component not published (e.g. draft product): no price, no total */
      this.$('[data-gift-price-hw]').textContent = '–';
      this.$('[data-gift-total]').textContent = '–';
    }
    this.$('[data-gift-facts]').textContent = `${tea.line === 'bio' ? 'TEABALLS BIO' : 'TEABALLS'}: ${tea.descriptor}`;

    const hwLink = this.$('[data-gift-part-hw]');
    if (choice.url) {
      hwLink.href = choice.url;
      hwLink.hidden = false;
    } else {
      hwLink.hidden = true;
    }
    this.$('[data-gift-part-tea]').href = tea.url;
    this.thumb(this.$('[data-gift-part-hw-img]'), choice.image);
    this.thumb(this.$('[data-gift-part-tea-img]'), tea.image);

    const ready = this.isReady();
    const button = this.$('[data-gift-add]');
    button.disabled = this.busy || this.locked || !ready;
    button.setAttribute('aria-busy', String(this.busy));
    button.textContent = this.busy ? this.words.adding : ready ? this.words.add : this.words.unavailable;
    if (!ready && !this.busy && !this.locked && (choice.id || tea.id)) this.say(this.words.stock, 'error', { quiet: true });
  }

  item(node, title, detail) {
    const strong = document.createElement('strong');
    strong.textContent = title;
    const small = document.createElement('small');
    small.textContent = detail;
    node.replaceChildren(strong, small);
  }

  thumb(node, url) {
    if (!node) return;
    node.replaceChildren();
    if (!url) return;
    const img = document.createElement('img');
    img.src = sized(url, 120);
    img.alt = '';
    img.width = 48;
    img.height = 48;
    img.loading = 'lazy';
    node.append(img);
  }

  renderMedia(choice, tea) {
    const frame = this.$('[data-gift-frame]');
    const signature = `${choice.key}|${tea.key}`;
    if (frame.dataset.signature === signature) return;
    frame.dataset.signature = signature;
    const combo = (this.cfg.combos || []).find((c) => c.choice === choice.key && c.tea === tea.key);
    const make = (url, alt, className, widths) => {
      const img = document.createElement('img');
      img.src = sized(url, widths[1]);
      img.srcset = widths.map((w) => `${sized(url, w)} ${w}w`).join(', ');
      img.sizes = className === 'tbg-builder__combo' ? '(max-width: 749px) calc(100vw - 48px), 560px' : '(max-width: 749px) 45vw, 280px';
      img.alt = alt || '';
      img.className = className;
      img.loading = 'lazy';
      img.decoding = 'async';
      return img;
    };
    frame.classList.toggle('is-combo', Boolean(combo));
    if (combo) {
      frame.replaceChildren(make(combo.image, combo.alt || `${choice.label} und ${tea.name}`, 'tbg-builder__combo', [500, 800, 1200]));
    } else {
      const parts = [];
      if (choice.image) parts.push(make(choice.image, choice.alt || choice.label, 'tbg-builder__pack', [300, 500, 700]));
      if (tea.image) parts.push(make(tea.image, tea.alt || tea.name, 'tbg-builder__pack tbg-builder__pack--tea', [300, 500, 700]));
      frame.replaceChildren(...parts);
    }
    this.$('[data-gift-caption]').textContent = `${choice.label} + ${tea.name}`;
  }

  say(text, kind = '', { quiet = false } = {}) {
    const node = this.$('[data-gift-msg]');
    node.replaceChildren();
    node.hidden = !text;
    node.dataset.kind = kind;
    if (!text) return;
    node.append(document.createTextNode(text));
    if (kind === 'uncertain') {
      const link = document.createElement('a');
      link.href = `${root()}cart`;
      link.textContent = this.words.cart;
      node.append(' ', link);
    }
    if (!quiet) node.focus({ preventScroll: true });
  }

  /* ---------- purchase ---------- */
  async revalidate(item) {
    const response = await fetch(`${root()}products/${encodeURIComponent(item.handle)}.js`, {
      cache: 'no-store',
      headers: { Accept: 'application/json' },
    });
    if (!response.ok) return { ok: false, reason: 'stock' };
    const product = await response.json();
    const variant = product.variants.find((v) => String(v.id) === String(item.id));
    if (!variant || !variant.available) {
      item.available = false;
      return { ok: false, reason: 'stock' };
    }
    item.compare = Number(variant.compare_at_price) || 0;
    if (Number(variant.price) !== Number(item.price)) {
      item.price = Number(variant.price);
      return { ok: false, reason: 'price' };
    }
    return { ok: true };
  }

  async submit() {
    if (this.busy || this.locked || !this.isReady()) return;
    if (window.__tbgGiftWriting) return;
    const { choice, tea } = this;
    this.say('');
    this.$('[data-gift-again]').hidden = true;
    this.busy = true;
    window.__tbgGiftWriting = true;
    this.render();
    try {
      for (const item of [choice, tea]) {
        const check = await this.revalidate(item);
        if (!check.ok) {
          const error = new Error(check.reason === 'price' ? this.words.price : this.words.stock);
          error.kind = check.reason;
          throw error;
        }
      }
      const token = newToken();
      const items = [choice, tea].map((item) => ({
        id: Number(item.id),
        quantity: 1,
        properties: { _tb_gift_selection: token },
      }));
      let timer;
      const timeout = new Promise((_, reject) => {
        timer = setTimeout(() => {
          const error = new Error(this.words.uncertain);
          error.uncertain = true;
          reject(error);
        }, 20000);
      });
      let cart;
      try {
        cart = await Promise.race([addPurchase(items), timeout]);
      } finally {
        clearTimeout(timer);
      }
      const confirmed = items.every(
        (expected) =>
          cart.items
            .filter(
              (line) =>
                String(line.variant_id) === String(expected.id) &&
                line.properties?._tb_gift_selection === token &&
                !line.selling_plan_allocation
            )
            .reduce((sum, line) => sum + Number(line.quantity), 0) === expected.quantity
      );
      if (!confirmed) {
        const error = new Error(this.words.uncertain);
        error.uncertain = true;
        throw error;
      }
      this.say(this.words.success, 'ok', { quiet: true });
      this.$('[data-gift-again]').hidden = false;
      track('gift_add_to_cart_result', { result: 'success', module: this.cfg.anchor, choice: choice.key, tea: tea.key });
    } catch (error) {
      if (error.uncertain) {
        this.locked = true;
        this.say(this.words.uncertain, 'uncertain');
      } else if (error.kind === 'price') {
        this.say(this.words.price, 'error');
      } else if (error.kind === 'stock') {
        this.say(this.words.stock, 'error');
      } else {
        this.say(this.words.failed, 'error');
      }
      track('gift_add_to_cart_result', {
        result: this.locked ? 'uncertain' : error.kind || 'error',
        module: this.cfg.anchor,
        choice: choice.key,
        tea: tea.key,
      });
    } finally {
      this.busy = false;
      window.__tbgGiftWriting = false;
      this.render();
      if (this.locked) {
        const button = this.$('[data-gift-add]');
        button.disabled = true;
      }
    }
  }
}

if (!customElements.get('tb-gift-builder')) customElements.define('tb-gift-builder', GiftBuilder);

/* smooth return to the gift boxes from "Weiteres Geschenk auswählen" keeps every module's state */
document.addEventListener('click', (event) => {
  const link = event.target.closest('[data-gift-again]');
  if (!link) return;
  const target = document.querySelector(link.getAttribute('href'));
  if (!target) return;
  event.preventDefault();
  target.scrollIntoView({ behavior: reducedMotion() ? 'auto' : 'smooth', block: 'start' });
});
